import React from "react";
import { ComposableMap, Geographies, Geography } from "react-simple-maps";
import indonesiaTopoJson from "@/assets/indonesia-provinces.json";

/**
 * Latar belakang peta Indonesia - versi modern & subtle.
 * Dipakai sebagai TEKSTUR dekoratif di belakang konten (bukan elemen utama),
 * jadi opacity dibuat rendah, garis provinsi tipis, dan warnanya gradasi lembut
 * supaya tidak "menabrak" kartu/konten di atasnya.
 */
export const IndonesiaMapBackground: React.FC<{
    className?: string;
    variant?: "dotted" | "solid" | "hybrid" | "vector";
}> = ({
    className = "",
}) => {
        return (
            <div className={`absolute inset-0 flex items-center justify-center pointer-events-none overflow-hidden ${className}`}>
                {/* Soft gradient glow blobs di belakang peta - aksen "kekinian" ala SaaS landing page */}
                <div className="absolute top-1/4 left-1/4 w-72 h-72 sm:w-96 sm:h-96 bg-blue-400/10 dark:bg-blue-500/10 rounded-full blur-3xl" />
                <div className="absolute bottom-1/4 right-1/4 w-72 h-72 sm:w-96 sm:h-96 bg-amber-400/10 dark:bg-cyan-400/10 rounded-full blur-3xl" />

                <div className="w-full h-full max-w-[96rem] flex items-center justify-center scale-105 sm:scale-100 transition-all duration-700">
                    <ComposableMap
                        projection="geoMercator"
                        projectionConfig={{
                            center: [118.5, -2.5],
                            scale: 1250,
                        }}
                        width={1000}
                        height={420}
                        className="w-full h-full select-none"
                        style={{ width: "100%", height: "100%" }}
                    >
                        <defs>
                            {/* Gradasi lembut biru -> indigo untuk isian pulau (mode terang) */}
                            <linearGradient id="map-fill-light" x1="0%" y1="0%" x2="100%" y2="100%">
                                <stop offset="0%" stopColor="#3b82f6" />
                                <stop offset="100%" stopColor="#6366f1" />
                            </linearGradient>
                            {/* Gradasi cyan -> biru menyala untuk mode gelap */}
                            <linearGradient id="map-fill-dark" x1="0%" y1="0%" x2="100%" y2="100%">
                                <stop offset="0%" stopColor="#22d3ee" />
                                <stop offset="100%" stopColor="#3b82f6" />
                            </linearGradient>
                            {/* Filter glow halus untuk mode gelap */}
                            <filter id="neon-glow-map" x="-50%" y="-50%" width="200%" height="200%">
                                <feGaussianBlur stdDeviation="3" result="blur" />
                                <feMerge>
                                    <feMergeNode in="blur" />
                                    <feMergeNode in="SourceGraphic" />
                                </feMerge>
                            </filter>
                        </defs>

                        <Geographies geography={indonesiaTopoJson}>
                            {({ geographies }) => (
                                <>
                                    {/* ============================================== */}
                                    {/* MODE TERANG: outline tipis + isian gradasi lembut, opacity rendah */}
                                    {/* ============================================== */}
                                    <g className="block dark:hidden">
                                        {geographies.map((geo) => (
                                            <Geography
                                                key={`light-${geo.rsmKey}`}
                                                geography={geo}
                                                fill="url(#map-fill-light)"
                                                stroke="#ffffff"
                                                strokeWidth={0.75}
                                                style={{
                                                    default: {
                                                        outline: "none",
                                                        opacity: 0.16,
                                                        transition: "opacity 0.3s ease",
                                                    },
                                                    hover: {
                                                        outline: "none",
                                                        opacity: 0.28,
                                                    },
                                                    pressed: { outline: "none" },
                                                }}
                                            />
                                        ))}
                                    </g>

                                    {/* ============================================== */}
                                    {/* MODE GELAP: outline menyala tipis dengan glow halus */}
                                    {/* ============================================== */}
                                    <g className="hidden dark:block">
                                        {geographies.map((geo) => (
                                            <Geography
                                                key={`dark-${geo.rsmKey}`}
                                                geography={geo}
                                                fill="url(#map-fill-dark)"
                                                stroke="#7dd3fc"
                                                strokeWidth={0.75}
                                                filter="url(#neon-glow-map)"
                                                style={{
                                                    default: {
                                                        outline: "none",
                                                        opacity: 0.14,
                                                        transition: "opacity 0.3s ease",
                                                    },
                                                    hover: {
                                                        outline: "none",
                                                        opacity: 0.26,
                                                    },
                                                    pressed: { outline: "none" },
                                                }}
                                            />
                                        ))}
                                    </g>
                                </>
                            )}
                        </Geographies>
                    </ComposableMap>
                </div>
            </div>
        );
    };
